/**
 * API helpers for verification sessions
 * Implements face verification and document verification API integration
 */

/**
 * HTTP error with status code preserved for caller error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly responseData?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

import type {
  CreateVerificationSessionRequest,
  CreateVerificationSessionResponse,
  CompleteVerificationSessionResponse,
  LegalIdVerificationResponse,
  SubmitRegulaLegalIdRequest,
} from "../types/verification";
import { addAPIBreadcrumb } from "../utils/sentryBreadcrumbs";
import { trackAPIError } from "../utils/sentryTracking";
import { getVerificationApiBaseUrl, getLegalIdVerificationApiBaseUrl } from "../utils/apiConfig";
import { logger } from "../utils/logger";

// Dynamic URL resolution - evaluates each time to pick up SDK config
function getApiUrl(): string {
  return getVerificationApiBaseUrl();
}

// Separate URL for legal ID verification (athena on 8082 locally, api.idme.cc on staging)
function getLegalIdApiUrl(): string {
  return getLegalIdVerificationApiBaseUrl();
}

/**
 * Create a new face verification session
 *
 * Calls POST /v1/verifications/faces with iProov provider
 * Based on mobile-sdk-ios implementation pattern
 *
 * @param params - Session creation parameters
 * @returns Verification session with iProov provider data (token, baseUrl)
 */
export async function createVerificationSession(
  params: CreateVerificationSessionRequest
): Promise<CreateVerificationSessionResponse> {
  const endpoint = "POST /v1/verifications/faces";
  logger.log("[API] Creating verification session:", params);

  // For face verification, call the faces endpoint
  if (params.flow === "face_liveness") {
    const requestBody: Record<string, unknown> = {
      externalReferenceId: params.externalReferenceId || `web_${Date.now()}`,
      providers: ["IPROOV"], // Uppercase as per backend API
    };

    // Include workflowId if provided
    if (params.workflowId) {
      requestBody.workflowId = params.workflowId;
    }

    // Track API request start
    addAPIBreadcrumb(endpoint, "pending", { flow: params.flow });

    let response: Response;
    try {
      response = await fetch(`${getApiUrl()}/v1/verifications/faces`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${params.sessionToken}`,
        },
        body: JSON.stringify(requestBody),
      });
    } catch (networkError) {
      // Track network errors (connection refused, DNS failure, etc.)
      addAPIBreadcrumb(endpoint, "network_error", { error: String(networkError) });
      trackAPIError(networkError, endpoint, { flow: params.flow });
      throw networkError;
    }

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      let errorData: any = null;

      try {
        errorData = await response.json();

        // Handle structured error format
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }

        // Include field-specific errors if present
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const fieldErrors = errorData.errors
            .map((err: any) => `${err.field}: ${err.message}`)
            .join('; ');
          errorMessage += ` (${fieldErrors})`;
        }

        logger.error("[API] Error response:", errorData);
      } catch {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage += ` - ${errorText}`;
          }
        } catch {
          // Ignore if we can't read the body
        }
      }

      // Track API error with Sentry
      const error = new Error(`Failed to create face verification session: ${errorMessage}`);
      addAPIBreadcrumb(endpoint, response.status, { errorMessage });
      trackAPIError(error, endpoint, {
        flow: params.flow,
        httpStatus: response.status,
        errorData,
      });

      throw error;
    }

    let responseData: any;
    try {
      responseData = await response.json();
    } catch (parseError) {
      const error = new Error("Failed to parse verification session response");
      addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
      trackAPIError(error, endpoint, { flow: params.flow });
      throw error;
    }
    logger.log("[API] Face verification session created:", responseData);
    logger.log("[API] Provider data:", responseData.providerData);

    // Track successful API call
    addAPIBreadcrumb(endpoint, response.status, { verificationId: responseData.uuid });

    // Handle new response structure: { uuid, providerData: { token, pod } }
    const providerData = responseData.providerData;

    if (!providerData || !providerData.token) {
      throw new Error("Invalid provider data in response - missing token");
    }

    // Construct baseUrl - prefer explicit baseUrl over pod construction
    // Note: API may return pod: "cluster" which is not a valid subdomain
    let baseUrl: string;
    if (providerData.baseUrl) {
      // Use explicit baseUrl from API (preferred)
      baseUrl = providerData.baseUrl;
      if (baseUrl.startsWith("wss://") || baseUrl.startsWith("ws://")) {
        // Fix WebSocket URL to HTTPS for iProov Web SDK
        const wsUrl = new URL(baseUrl);
        baseUrl = `https://${wsUrl.host}`;
        logger.log("[API] Converted WebSocket baseUrl to HTTPS:", {
          original: providerData.baseUrl,
          converted: baseUrl
        });
      } else {
        logger.log("[API] Using baseUrl from API:", baseUrl);
      }
    } else if (providerData.pod && providerData.pod !== "cluster") {
      // Fallback: construct from pod (only if pod is valid, not "cluster")
      // e.g., pod: "edge02.eu4" -> "https://edge02.eu4.rp.secure.iproov.me"
      baseUrl = `https://${providerData.pod}.rp.secure.iproov.me`;
      logger.log("[API] Constructed baseUrl from pod:", {
        pod: providerData.pod,
        baseUrl: baseUrl
      });
    } else {
      throw new Error("Invalid provider data in response - missing valid baseUrl or pod");
    }

    return {
      verificationId: responseData.uuid,
      vendorToken: providerData.token,
      baseUrl: baseUrl,
      providerData: {
        provider: "IPROOV",
        token: providerData.token,
        baseUrl: baseUrl,
        verificationId: responseData.uuid,
        idmeResponse: responseData,
      },
    };
  }

  // Document verification flow
  if (params.flow === "legal_id_verification") {
    const endpoint = "POST /v1/verifications/legal-ids";

    const requestBody: Record<string, unknown> = {
      providers: ["veriff"],
      externalReferenceId: params.externalReferenceId || `web_${Date.now()}`,
      submissionMode: "PRE_CAPTURED",
    };

    if (params.workflowId) {
      requestBody.workflowId = params.workflowId;
    }

    addAPIBreadcrumb(endpoint, "pending", { flow: params.flow });

    let response: Response;
    try {
      response = await fetch(`${getLegalIdApiUrl()}/v1/verifications/legal-ids`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${params.sessionToken}`,
          "X-Client-Type": "web-sdk",
        },
        body: JSON.stringify(requestBody),
      });
    } catch (networkError) {
      addAPIBreadcrumb(endpoint, "network_error", { error: String(networkError) });
      trackAPIError(networkError, endpoint, { flow: params.flow });
      throw networkError;
    }

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      let errorData: any = null;
      try {
        errorData = await response.json();
        if (errorData.description) errorMessage = errorData.description;
        if (errorData.detail) errorMessage = errorData.detail;
      } catch { /* ignore parse errors */ }

      const statusCode = errorData?.status ?? response.status;
      const error = new ApiError(
        `Failed to create document verification session: ${errorMessage}`,
        statusCode,
        errorData
      );
      addAPIBreadcrumb(endpoint, statusCode, { errorMessage });
      trackAPIError(error, endpoint, { flow: params.flow, httpStatus: statusCode, errorData });
      throw error;
    }

    let responseData: any;
    try {
      responseData = await response.json();
    } catch (parseError) {
      const error = new Error("Failed to parse document verification session response");
      addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
      trackAPIError(error, endpoint, { flow: params.flow });
      throw error;
    }

    logger.log("[API] Document verification session created:", responseData);

    // Extract session URL from nested providerData.verification.url
    // Confirmed by mobile-sdk-ios VerificationsManager.swift:519-522
    const veriffProviderData = responseData.providerData;
    const veriffVerification = veriffProviderData?.verification;
    const sessionUrl = veriffVerification?.url;

    if (!sessionUrl) {
      throw new Error("Invalid provider data in response - missing providerData.verification.url");
    }

    addAPIBreadcrumb(endpoint, response.status, { verificationId: responseData.uuid });

    return {
      verificationId: responseData.uuid,
      vendorToken: sessionUrl, // Full Veriff session URL passed directly to createVeriffFrame
    };
  }

  throw new Error(`Unsupported verification flow: ${params.flow}`);
}

/**
 * Get verification status by polling the API
 *
 * Calls GET /v1/verifications/faces/{verificationId}
 * Based on mobile-sdk-ios FaceLivenessManager pattern
 *
 * @param verificationId - Verification session ID
 * @param sessionToken - Authorization token
 * @returns Verification result with status
 */
export async function getVerificationStatus(
  verificationId: string,
  sessionToken: string
): Promise<CompleteVerificationSessionResponse> {
  const endpoint = `GET /v1/verifications/faces/${verificationId}`;
  logger.log("[API] Getting verification status:", verificationId);

  // Track API request start
  addAPIBreadcrumb(endpoint, "pending", { verificationId });

  let response: Response;
  try {
    response = await fetch(`${getApiUrl()}/v1/verifications/faces/${verificationId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`,
      },
    });
  } catch (networkError) {
    // Track network errors
    addAPIBreadcrumb(endpoint, "network_error", { verificationId, error: String(networkError) });
    trackAPIError(networkError, endpoint, { verificationId });
    throw networkError;
  }

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    let errorData: any = null;

    try {
      errorData = await response.json();

      // Handle structured error format
      if (errorData.detail) {
        errorMessage = errorData.detail;
      }

      // Include field-specific errors if present
      if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        const fieldErrors = errorData.errors
          .map((err: any) => `${err.field}: ${err.message}`)
          .join('; ');
        errorMessage += ` (${fieldErrors})`;
      }

      logger.error("[API] Error response:", errorData);
    } catch {
      // If JSON parsing fails, try to get text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage += ` - ${errorText}`;
        }
      } catch {
        // Ignore if we can't read the body
      }
    }

    // Track API error with Sentry
    const error = new Error(`Failed to get verification status: ${errorMessage}`);
    addAPIBreadcrumb(endpoint, response.status, { verificationId, errorMessage });
    trackAPIError(error, endpoint, {
      verificationId,
      httpStatus: response.status,
      errorData,
    });

    throw error;
  }

  let responseData: any;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error("Failed to parse verification status response");
    addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
    trackAPIError(error, endpoint, { verificationId });
    throw error;
  }
  logger.log("[API] Verification status retrieved:", responseData);

  // Track successful API call
  addAPIBreadcrumb(endpoint, response.status, {
    verificationId,
    status: responseData.status,
    outcome: responseData.outcome,
  });

  // Return the complete API response as-is
  return responseData;
}

/**
 * Complete a verification session with vendor results (deprecated for iProov)
 *
 * Note: For iProov flow, the verification happens server-side.
 * After iProov SDK completes, poll getVerificationStatus() instead.
 *
 * @param verificationId - Verification session ID
 * @param vendorResult - Results from vendor SDK
 * @returns API response
 */
export async function completeVerificationSession(
  verificationId: string,
  vendorResult: unknown
): Promise<CompleteVerificationSessionResponse> {
  logger.log("[API] completeVerificationSession called with:", {
    verificationId,
    vendorResult,
  });

  // For iProov flow, this is not used - verification is completed server-side
  // This stub is kept for backward compatibility
  return {
    uuid: verificationId,
    type: "FACE_VERIFICATION",
    status: "completed",
    outcome: "approved",
    results: [],
  };
}

/**
 * Get legal ID verification status by polling the API
 *
 * Calls GET /v1/verifications/legal-ids/{verificationId}
 * Mirrors getVerificationStatus() for face verification.
 * Poll until outcome !== "pending" (mirrors mobile-sdk-ios EndpointPoller pattern).
 *
 * @param verificationId - Verification session ID (uuid from POST response)
 * @param sessionToken - Authorization token
 * @returns Legal ID verification result with status and outcome
 */
export async function getLegalIdVerificationStatus(
  verificationId: string,
  sessionToken: string
): Promise<{ data: LegalIdVerificationResponse; statusCode: number }> {
  const endpoint = `GET /v1/verifications/legal-ids/${verificationId}`;
  logger.log("[API] Getting legal ID verification status:", verificationId);

  addAPIBreadcrumb(endpoint, "pending", { verificationId });

  let response: Response;
  try {
    response = await fetch(`${getLegalIdApiUrl()}/v1/verifications/legal-ids/${verificationId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`,
      },
    });
  } catch (networkError) {
    addAPIBreadcrumb(endpoint, "network_error", { verificationId, error: String(networkError) });
    trackAPIError(networkError, endpoint, { verificationId });
    throw networkError;
  }

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    let errorData: any = null;
    try {
      errorData = await response.json();
      if (errorData.description) errorMessage = errorData.description;
      if (errorData.detail) errorMessage = errorData.detail;
    } catch { /* ignore parse errors */ }

    const statusCode = errorData?.status ?? response.status;
    const error = new ApiError(
      `Failed to get legal ID verification status: ${errorMessage}`,
      statusCode,
      errorData
    );
    addAPIBreadcrumb(endpoint, statusCode, { verificationId, errorMessage });
    trackAPIError(error, endpoint, { verificationId, httpStatus: statusCode, errorData });
    throw error;
  }

  let responseData: any;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error("Failed to parse legal ID verification status response");
    addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
    trackAPIError(error, endpoint, { verificationId });
    throw error;
  }

  logger.log("[API] Legal ID verification status retrieved:", responseData);

  addAPIBreadcrumb(endpoint, response.status, {
    verificationId,
    status: responseData.status,
    outcome: responseData.outcome,
  });

  return { data: responseData, statusCode: response.status };
}

/**
 * Accept a legal ID verification (user confirmation)
 *
 * Calls PATCH /v1/verifications/legal-ids/{id}
 * Called after user confirms their extracted ID details are correct.
 *
 * @returns { status: "accepted" }
 */
export async function patchLegalIdVerification(
  verificationId: string,
  sessionToken: string
): Promise<{ status: string }> {
  const endpoint = `PATCH /v1/verifications/legal-ids/${verificationId}`;
  logger.log("[API] Accepting legal ID verification:", verificationId);

  addAPIBreadcrumb(endpoint, "pending", { verificationId });

  let response: Response;
  try {
    response = await fetch(`${getLegalIdApiUrl()}/v1/verifications/legal-ids/${verificationId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ status: "accepted" }),
    });
  } catch (networkError) {
    addAPIBreadcrumb(endpoint, "network_error", { verificationId, error: String(networkError) });
    trackAPIError(networkError, endpoint, { verificationId });
    throw networkError;
  }

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    let errorData: any = null;
    try {
      errorData = await response.json();
      if (errorData.description) errorMessage = errorData.description;
      if (errorData.detail) errorMessage = errorData.detail;
    } catch { /* ignore parse errors */ }

    const statusCode = errorData?.status ?? response.status;
    const error = new ApiError(
      `Failed to accept legal ID verification: ${errorMessage}`,
      statusCode,
      errorData
    );
    addAPIBreadcrumb(endpoint, statusCode, { verificationId, errorMessage });
    trackAPIError(error, endpoint, { verificationId, httpStatus: statusCode });
    throw error;
  }

  addAPIBreadcrumb(endpoint, response.status, { verificationId });

  // 204 No Content — success with no body
  if (response.status === 204) {
    return { status: "accepted" };
  }

  const responseData = await response.json();
  addAPIBreadcrumb(endpoint, response.status, { verificationId, status: responseData.status });
  return responseData;
}

/**
 * athena's `media.imageFront`/`imageBack` are OpenAPI `format: byte` — raw base64 with NO
 * `data:image/...;base64,` prefix. The Regula locator already returns prefix-free base64; strip any
 * `data:` prefix defensively before sending in case a future capture path includes one.
 */
function stripDataUriPrefix(base64: string): string {
  return base64.replace(/^data:[^;,]*;base64,/, "");
}

/**
 * Submit Regula-captured document images for legal ID verification.
 *
 * Calls POST /v1/verifications/legal-ids in athena's REGULA UPLOAD mode. Unlike the Veriff path
 * (which creates a session and lets Veriff upload images to its own servers), Regula captures
 * client-side and we forward the uncropped front (+ optional back) as raw base64. The create is
 * asynchronous: athena returns status "initiated" / outcome "pending" with no providerData and
 * runs Regula on a background thread — so callers then poll getLegalIdVerificationStatus().
 *
 * Contract: submissionMode "UPLOAD", providers ["regula"], media {imageFront, imageBack?}.
 * At least one image is required; back is optional.
 *
 * @param params - Session token, uncropped images (raw base64), and optional reference/workflow ids
 * @returns The initiated verification session ({ data, statusCode }), mirroring getLegalIdVerificationStatus()
 */
export async function submitRegulaLegalIdVerification(
  params: SubmitRegulaLegalIdRequest
): Promise<{ data: LegalIdVerificationResponse; statusCode: number }> {
  const endpoint = "POST /v1/verifications/legal-ids";
  logger.log("[API] Submitting Regula legal ID verification (UPLOAD mode)");

  const imageFront = params.imageFront ? stripDataUriPrefix(params.imageFront) : "";
  const imageBack = params.imageBack ? stripDataUriPrefix(params.imageBack) : "";

  // athena requires at least one image for UPLOAD mode. Our flow always has a front; guard anyway
  // so an empty capture fails fast with a clear message instead of a 400 round-trip.
  if (!imageFront && !imageBack) {
    throw new Error("Cannot submit Regula verification: no document image was captured");
  }

  const media: Record<string, string> = {};
  if (imageFront) media.imageFront = imageFront;
  if (imageBack) media.imageBack = imageBack;

  const requestBody: Record<string, unknown> = {
    submissionMode: "UPLOAD",
    providers: ["regula"],
    media,
    externalReferenceId: params.externalReferenceId || `web_${Date.now()}`,
  };

  if (params.workflowId) {
    requestBody.workflowId = params.workflowId;
  }

  addAPIBreadcrumb(endpoint, "pending", { flow: "legal_id_verification", provider: "regula" });

  let response: Response;
  try {
    response = await fetch(`${getLegalIdApiUrl()}/v1/verifications/legal-ids`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${params.sessionToken}`,
        "X-Client-Type": "web-sdk",
      },
      body: JSON.stringify(requestBody),
    });
  } catch (networkError) {
    addAPIBreadcrumb(endpoint, "network_error", { provider: "regula", error: String(networkError) });
    trackAPIError(networkError, endpoint, { flow: "legal_id_verification" });
    throw networkError;
  }

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    let errorData: any = null;
    try {
      errorData = await response.json();
      if (errorData.description) errorMessage = errorData.description;
      if (errorData.detail) errorMessage = errorData.detail;
    } catch { /* ignore parse errors */ }

    const statusCode = errorData?.status ?? response.status;
    const error = new ApiError(
      `Failed to submit Regula legal ID verification: ${errorMessage}`,
      statusCode,
      errorData
    );
    addAPIBreadcrumb(endpoint, statusCode, { provider: "regula", errorMessage });
    trackAPIError(error, endpoint, { flow: "legal_id_verification", httpStatus: statusCode, errorData });
    throw error;
  }

  let responseData: any;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error("Failed to parse Regula legal ID verification response");
    addAPIBreadcrumb(endpoint, "parse_error", { provider: "regula", error: String(parseError) });
    trackAPIError(error, endpoint, { flow: "legal_id_verification" });
    throw error;
  }

  logger.log("[API] Regula legal ID verification submitted:", responseData.uuid);

  addAPIBreadcrumb(endpoint, response.status, {
    verificationId: responseData.uuid,
    status: responseData.status,
    outcome: responseData.outcome,
  });

  return { data: responseData, statusCode: response.status };
}
